import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Breadcrumbs,
  Link,
  Skeleton,
  Alert,
  Button,
  useTheme,
  useMediaQuery,
  Fade,
  Divider
} from '@mui/material';
import {
  Home as HomeIcon,
  Policy as PolicyIcon,
  Security as SecurityIcon,
  Cookie as CookieIcon,
  Refresh as RefreshIcon,
  Print as PrintIcon,
  Share as ShareIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient, { OutApiResponse } from '../../../../providers/axios.config'; // Adjust path as needed
import { formatDate } from '../../../../utils/helpers';

// Define allowed static content types as a string union
type StaticDataType = 'terms-and-conditions' | 'privacy-policy' | 'cookie-policy';

type PaletteColorKey = 'primary' | 'warning' | 'info';

// Mapping object for allowed types
const STATIC_DATA_TYPES: Record<StaticDataType, StaticDataType> = {
  'terms-and-conditions': 'terms-and-conditions',
  'privacy-policy': 'privacy-policy',
  'cookie-policy': 'cookie-policy'
};

export const AgreementsPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { type } = useParams(); // Get type from URL params
  
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Validate and get content type
  const contentType = STATIC_DATA_TYPES[type as StaticDataType];
  
  // Content metadata
  const getContentMetadata = () => {
    switch (contentType) {
      case 'cookie-policy':
        return {
          title: t('cookie_policy_title', 'Cookie Policy'),
          icon: <CookieIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
          description: t('cookie_policy_desc', 'Learn about how we use cookies on Layerstock.com'),
          color: 'warning'
        };
      case 'privacy-policy':
        return {
          title: t('privacy_policy_title', 'Privacy Policy'),
          icon: <SecurityIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
          description: t('privacy_policy_desc', 'Our commitment to protecting your personal information'),
          color: 'info'
        };
      case 'terms-and-conditions':
        return {
          title: t('terms_conditions_title', 'Terms and Conditions'),
          icon: <PolicyIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
          description: t('terms_conditions_desc', 'Terms of service and user agreement'),
          color: 'primary'
        };
      default:
        return {
          title: t('static_content_title', 'Legal Information'),
          icon: <PolicyIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
          description: t('static_content_desc', 'Legal information and policies'),
          color: 'primary'
        };
    }
  };

  // Fetch content from API
  const fetchContent = async () => {
    if (!contentType) {
      setError(t('invalid_content_type', 'Invalid content type'));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Make API call with the type parameter
      const response: any = await apiClient.get<OutApiResponse>(`agreements/${contentType}`);
      
      if (response.status === 'success') {
        setContent(response.data.content || response.data); // Handle different response structures
        setLastUpdated(response.data.updated_at || new Date().toISOString());
      } else {
        throw new Error(response.message || t('failed_to_fetch_content', 'Failed to fetch content'));
      }
    } catch (err: any) {
      setError(err.message || t('error_loading_content', 'Error loading content'));
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000)
    }
  };

  // Fetch content on component mount and type change
  useEffect(() => {
    fetchContent();
  }, [contentType]);

  // Redirect to 404 if invalid type
  useEffect(() => {
    if (type && !contentType) {
      navigate('/404', { replace: true });
    }
  }, [type, contentType, navigate]);

  // Handle print
  const handlePrint = () => {
    window.print();
  };

  // Handle share
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: metadata.title,
          text: metadata.description,
          url: window.location.href,
        });
      } catch (err) {
        console.log('Error sharing:', err);
      }
    } else {
      // Fallback: copy URL to clipboard
      navigator.clipboard.writeText(window.location.href);
      // You could show a toast notification here
    }
  };

  const metadata = getContentMetadata();

  // Loading skeleton
  const renderSkeleton = () => (
    <Container maxWidth={'xl'} disableGutters sx={{ px: 3 }}>
      <Box sx={{ py: 4 }}>
        <Skeleton variant="text" width={200} height={40} sx={{ mb: 2 }} />
        <Paper elevation={1} sx={{ p: 4, mb: 4 }}>
          <Skeleton variant="circular" width={60} height={60} sx={{ mx: 'auto', mb: 2 }} />
          <Skeleton variant="text" height={60} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="60%" height={30} sx={{ mx: 'auto' }} />
        </Paper>
        <Paper elevation={1} sx={{ p: 4 }}>
          {[...Array(8)].map((_, index) => (
            <Skeleton key={index} variant="text" height={20} sx={{ mb: 1 }} />
          ))}
        </Paper>
      </Box>
    </Container>
  );

  // Error state
  const renderError = () => (
    <Container maxWidth={'xl'} disableGutters sx={{ px: 3 }}>
      <Box sx={{ py: 4 }}>
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={() => fetchContent()} startIcon={<RefreshIcon />}>
              {t('retry', 'Retry')}
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    </Container>
  );

  if (loading) return renderSkeleton();
  if (error) return renderError();

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
      <Container maxWidth={'xl'} sx={{ py: 4 }}>
        {/* Header */}
        <Fade in={true} timeout={600}>
          <Paper 
            elevation={2}
            sx={{ 
              p: 4, 
              mb: 4, 
              textAlign: 'center',
              background: `linear-gradient(135deg, ${theme.palette[metadata.color as PaletteColorKey].main}10, ${theme.palette[metadata.color as PaletteColorKey].light}20)`
            }}
          >
            {metadata.icon}
            <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 600, mt: 2 }}>
              {metadata.title}
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto', mb: 2 }}>
              {metadata.description}
            </Typography>
            
            {lastUpdated && (
              <Typography variant="caption" color="text.secondary">
                {t('last_updated', 'Last updated')}: {formatDate(lastUpdated)}
              </Typography>
            )}
          </Paper>
        </Fade>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 4 }}>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            sx={{ 
              '@media print': { display: 'none' },
              fontWeight: 500
            }}
          >
            {t('print', 'Print')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<ShareIcon />}
            onClick={handleShare}
            sx={{ 
              '@media print': { display: 'none' },
              fontWeight: 500
            }}
          >
            {t('share', 'Share')}
          </Button>
        </Box>

        {/* Content */}
        <Fade in={true} timeout={800}>
          <Paper 
            elevation={1} 
            sx={{ 
              overflow: 'hidden',
              '@media print': {
                boxShadow: 'none',
                border: 'none'
              }
            }}
          >
            <Box
              sx={{
                p: 4,
                '& h1, & h2, & h3, & h4, & h5, & h6': {
                  fontFamily: theme.typography.fontFamily,
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  marginTop: theme.spacing(3),
                  marginBottom: theme.spacing(2),
                  lineHeight: 1.3,
                  '&:first-of-type': {
                    marginTop: 0
                  }
                },
                '& h1': { fontSize: '2rem' },
                '& h2': { fontSize: '1.75rem' },
                '& h3': { fontSize: '1.5rem' },
                '& h4': { fontSize: '1.25rem' },
                '& p': {
                  fontFamily: theme.typography.fontFamily,
                  fontSize: '1rem',
                  lineHeight: 1.7,
                  color: theme.palette.text.primary,
                  marginBottom: theme.spacing(2),
                  textAlign: 'justify'
                },
                // FIXED LIST STYLES - This handles your specific Quill output correctly
                '& ul': {
                  paddingLeft: theme.spacing(3),
                  marginBottom: theme.spacing(2),
                  listStyleType: 'disc',
                  '& li': {
                    marginBottom: theme.spacing(0.5),
                    lineHeight: 1.6
                  }
                },
                '& ol': {
                  paddingLeft: theme.spacing(3),
                  marginBottom: theme.spacing(2),
                  listStyleType: 'decimal', // Default to numeric for main lists
                  '& li': {
                    marginBottom: theme.spacing(0.5),
                    lineHeight: 1.6,
                    paddingLeft: 0
                  }
                },
                
                // Handle nested ordered lists (proper nesting)
                '& ol ol': {
                  listStyleType: 'lower-alpha',
                  counterReset: 'sub-list-counter',
                  paddingLeft: theme.spacing(4),
                  marginTop: theme.spacing(1),
                  marginBottom: theme.spacing(1),
                  '& li': {
                    paddingLeft: theme.spacing(1),
                    marginBottom: theme.spacing(0.5)
                  }
                },
                
                // Third level nesting (if needed)
                '& ol ol ol': {
                  listStyleType: 'lower-roman',
                  counterReset: 'sub-sub-list-counter',
                  paddingLeft: theme.spacing(4),
                  '& li': {
                    paddingLeft: theme.spacing(1)
                  }
                },
                
                '& ol li': {
                  counterIncrement: 'main-list'
                },
                
                // For alphabetic indented items - don't reset counter on each item
                '& ol li.ql-indent-1': {
                  listStyleType: 'none', // Remove default marker
                  marginLeft: theme.spacing(4),
                  paddingLeft: theme.spacing(1),
                  counterIncrement: 'indent-alpha-counter',
                  position: 'relative',
                  '&::before': {
                    content: 'counter(indent-alpha-counter, lower-alpha) ". "',
                    position: 'absolute',
                    left: '-1.5em',
                    fontWeight: 'normal'
                  }
                },
                
                // Reset the alphabetic counter when we encounter the first indent-1 item
                '& ol li:not(.ql-indent-1) + li.ql-indent-1': {
                  counterReset: 'indent-alpha-counter',
                  counterIncrement: 'indent-alpha-counter'
                },
                
                '& ol li.ql-indent-2': {
                  listStyleType: 'none',
                  marginLeft: theme.spacing(6),
                  paddingLeft: theme.spacing(1),
                  counterIncrement: 'indent-roman-counter',
                  '&::before': {
                    content: 'counter(indent-roman-counter, lower-roman) ". "',
                    position: 'absolute',
                    left: '-1.5em'
                  }
                },
                
                // Handle other nested combinations
                '& ul ul, & ol ul, & ul ol': {
                  paddingLeft: theme.spacing(4),
                  marginTop: theme.spacing(0.5),
                  marginBottom: theme.spacing(0.5),
                  '& li': {
                    paddingLeft: theme.spacing(1)
                  }
                },
                // Alternative approach: Force all ordered lists to be alphabetic
                // Uncomment this if you want ALL ordered lists to be alphabetic
                // '& ol:not([class*="numeric"]):not([data-list="numeric"])': {
                //   listStyleType: 'lower-alpha'
                // },
                '& a': {
                  color: theme.palette.primary.main,
                  textDecoration: 'underline',
                  '&:hover': {
                    color: theme.palette.primary.dark
                  }
                },
                '& strong, & b': {
                  fontWeight: 600
                },
                '& table': {
                  width: '100%',
                  borderCollapse: 'collapse',
                  marginBottom: theme.spacing(2),
                  '& th, & td': {
                    border: `1px solid ${theme.palette.divider}`,
                    padding: theme.spacing(1),
                    textAlign: 'left'
                  },
                  '& th': {
                    backgroundColor: theme.palette.grey[100],
                    fontWeight: 600
                  }
                },
                '& blockquote': {
                  borderLeft: `4px solid ${theme.palette.primary.main}`,
                  paddingLeft: theme.spacing(2),
                  marginLeft: 0,
                  marginRight: 0,
                  fontStyle: 'italic',
                  backgroundColor: theme.palette.grey[50],
                  padding: theme.spacing(2),
                  borderRadius: 1
                },
                '& hr': {
                  border: 'none',
                  height: 1,
                  backgroundColor: theme.palette.divider,
                  margin: theme.spacing(3, 0)
                }
              }}
              dangerouslySetInnerHTML={{ __html: content }}
            />
          </Paper>
        </Fade>

        {/* Footer */}
        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <Divider sx={{ mb: 3 }} />
          <Typography variant="body2" color="text.secondary">
            {t('questions_contact', 'If you have any questions about this policy, please contact us at')}{' '}
            <Link href="mailto:support@layerstock.com" color="primary">
              support@layerstock.com
            </Link>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};